import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ListSubheader,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatRecordDateTime, toDateTimeInputValue } from '../../library/dateTimeHelper';
import { splitApiError } from '../../library/useApiFormError';
import {
    AppointmentStatus,
    canAnswer,
    canReschedule,
    hasLapsed,
    IAppointment,
    IAppointmentPaymentOptions,
    isPendingRequest,
} from '../../library/models/appointment';
import { useRefreshDiary } from '../../library/useRefreshDiary';
import { bookableSessions, IPatientPurchase } from '../../library/models/patientPurchase';
import { IService } from '../../library/models/service';
import { localised } from '../../library/models/serviceCatalog';
import { formatPrice, planLabel } from '../../library/purchaseDisplay';
import { AddPurchaseDialog } from '../pages/PatientDetail/AddPurchaseDialog';
import { useIsCompact } from '../../library/useIsCompact';
import ResourcesIds from '../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../services/queryService';
import { services } from '../../services/services';
import { clockOf } from '../pages/MyCalendar/scheduleEntries';
import { ConfirmationDialog } from '../confirmationDialog/confirmationDialog';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from '../layout/snackbarProvider';
import { RescheduleDialog } from './RescheduleDialog';
import { DeclineRequestDialog } from './DeclineRequestDialog';
import { detailRowStyles } from './appointmentDetail.styles';
import { PersonLabel } from '../userAvatar/UserAvatar';

/**
 * Whether the resolving row applies: a booking to mark, or a terminal row to reopen. Not a
 * request, which is answered rather than resolved, and not a declined one, which is final.
 */
const isResolvable = (appointment: IAppointment): boolean =>
    appointment.status !== 'REQUESTED' && appointment.status !== 'DECLINED';

/**
 * The two shapes {@link AppointmentDetailDialog}'s picker can hold.
 *
 * A plan carries its id; *pays at the end* carries nothing, because what will pay for the
 * session does not exist yet -- picking it opens the sale that creates it.
 */
const PLAN = 'plan:';
const PAY_AT_END = 'payAtEnd';

export interface IAppointmentDetailDialogProps {
    /** The appointment on show, or null when the dialog is closed. */
    appointment: IAppointment | null;
    onClose: () => void;
    /** Something was resolved or removed: the host refetches whatever list it drew this from. */
    onChanged: () => void;
}

/**
 * Everything that can be done to one appointment, wherever it was found.
 *
 * This is deliberately one component rather than one per page. My Calendar drew it and
 * owned every verb, so the only way to reopen a booking cancelled by mistake was to turn
 * on *Show cancelled* and find the right day -- while My Appointments, which is the page
 * that actually holds the history, could do nothing but cancel a future session. Two
 * copies of this gating would have drifted the first time either page changed, and the
 * purchase rule below is not a rule worth writing twice.
 *
 * Three things it owns are load-bearing:
 *
 * - **Marking a session held is also settling it.** The clinic takes payment as the session
 *   ends, or at the end of the first session of a package, so the therapist names what it
 *   comes out of right here. A row booked against a package bought earlier already knows
 *   and asks nothing.
 * - **A terminal row is put back to SCHEDULED first.** There is no held-to-no-show step:
 *   reopening is how a mis-click is undone without deleting a real appointment.
 * - **A block of leave is none of that.** It has no patient, no purchase and no status to
 *   answer for; the one thing it can be is wrong, so it is removed outright. My
 *   Appointments never passes one -- a block belongs to nobody and its history is nobody's
 *   -- so that branch only ever runs for the calendar that drew it.
 *
 * **A patient can open this now, and that is what makes the gating below load-bearing.**
 * Every verb here except *Change time* is `PUT /appointments/{id}/status` or a delete, both
 * THERAPIST/ADMIN -- so drawing them for a patient is drawing buttons that answer "the
 * appointment could not be updated", which is the defect My Appointments' own Cancel button
 * used to be. They are behind `isStaff` for that reason and not for tidiness.
 *
 * *Change time* is the one verb with a wider rule, because the API has a wider rule for it:
 * staff may move anything, and anybody else may move what they booked themselves while it
 * is still scheduled. `canReschedule` is that rule, and the server decides it identically --
 * so this never draws a control the API would refuse.
 *
 * **A patient's booking request is answered here, and only by the therapist it names.**
 * *Confirm* and *Decline* are drawn for that one person (`canAnswer`), because the API
 * refuses anybody else -- an admin included -- and "confirmed" in the patient's inbox has to
 * mean the therapist they chose said yes. Everybody else on staff is told whose answer it is
 * waiting for and may only call it off. None of *Mark as held*, *Did not attend* or
 * *Reopen* applies to a request, and a declined one is final: nothing is drawn to reopen it.
 */
export const AppointmentDetailDialog = (props: IAppointmentDetailDialogProps): JSX.Element => {
    const { appointment, onClose, onChanged } = props;
    const { t: translate, i18n } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const isCompact = useIsCompact();
    const { user, role } = useAuth();

    /**
     * From the role, never from the row. The API reads the same distinction out of the
     * token's authorities, so the two cannot disagree about who is staff.
     */
    const isStaff: boolean = role === 'THERAPIST' || role === 'ADMIN';

    /**
     * Whether an open request is past its deadline, on the viewer's own wall clock -- the same
     * reading `MyCalendar` washes the past with. The API is the authority and refuses a late
     * confirmation whatever this says; this only stops offering a button that would be refused.
     */
    const lapsed: boolean = !!appointment && hasLapsed(appointment, toDateTimeInputValue(new Date()));

    /**
     * What was picked, as `plan:<id>` or the literal {@link PAY_AT_END}.
     *
     * One discriminated string rather than a purchase id and a flag beside it, mirroring
     * `NewEntryDialog`'s own select -- the two dialogs ask the same question and should
     * read the same way. Two fields would also be free to disagree, and one of the states
     * they can reach is "pay at the end, against plan 12".
     */
    const [choice, setChoice] = useState<string>('');
    const [isSelling, setIsSelling] = useState<boolean>(false);
    const [isRemovingBlock, setIsRemovingBlock] = useState<boolean>(false);
    const [isMoving, setIsMoving] = useState<boolean>(false);
    const [isDeclining, setIsDeclining] = useState<boolean>(false);

    /**
     * Whether the picker is on screen at all.
     *
     * It used to be, always, from the moment a card opened -- so a therapist who came to
     * read an hour, move it or call it off was asked a money question about a session that
     * had not happened, with the primary button dead until they answered it. The question
     * is real but it belongs to the *hold*, which is the only verb that needs an answer.
     */
    const [isChoosingPurchase, setIsChoosingPurchase] = useState<boolean>(false);

    // A different appointment is a different question, so the answer to the last one is
    // dropped. The host clears its own selection, but the dialog is reused rather than
    // remounted, and a purchase picked for one patient must never reach another's.
    useEffect(() => {
        setChoice('');
        setIsSelling(false);
        setIsChoosingPurchase(false);
    }, [appointment?.id]);

    const resolve = useReactMutation(services.appointments.resolveAppointmentMutation());
    const confirm = useReactMutation(services.appointments.confirmRequestMutation());
    const refreshDiary = useRefreshDiary();

    /**
     * The host's own refresh and everything else the diary draws. A request answered here is
     * also on the strip above the calendar and in the count in the header, neither of which
     * this dialog's host knows about.
     */
    const changed = (): void => {
        onChanged();
        refreshDiary();
    };

    /**
     * A real delete, and the only one here.
     *
     * A booking that will not happen is CANCELLED, so that it stays visible and the clinic
     * can still see it was called off. A block of leave has nothing to answer for -- it is
     * either time the therapist is away or a row somebody entered by mistake.
     */
    const removeBlock = useReactMutation(services.appointments.deleteAppointmentMutation());

    /**
     * What may pay for this session.
     *
     * Only fetched when the open appointment does not already name a purchase -- a session
     * booked against a package bought weeks ago needs nothing chosen here.
     *
     * The server splits the plans by discipline rather than this list being filtered here,
     * because the split cannot be made here: which discipline a plan belongs to is its
     * catalogue option resolved up to a type, and `getServices()` answers with singles
     * only, so a package's own option is never in the list this app holds. Filtering on
     * what *was* available -- usable, with sessions left -- is how a Kinetoterapie package
     * came to be the only thing offered to settle a Masaj session.
     *
     * Asked for as the card opens rather than when the therapist presses *Mark as held*,
     * even though nothing is drawn from it until then: the press has to know at once
     * whether the patient has anything to draw down, since that is what decides whether it
     * opens a picker or the sale. Deciding on an answer that has not arrived yet would
     * make the button hesitate, or worse, guess.
     */
    const { data: options, isFetched: knowsWhatCouldPay } =
        useReactQuery<IAppointmentPaymentOptions>(
            services.appointments.getPaymentOptions(appointment?.id ?? 0),
            { enabled: !!appointment?.patientId && !appointment?.purchaseId }
        );

    const matching: IPatientPurchase[] = options?.matching ?? [];
    const payNow: IService | null = options?.payNow ?? null;

    /** Already settled, or the therapist has just picked what settles it. */
    const needsAPurchase: boolean = !!appointment && !appointment.purchaseId;

    /**
     * *Pays at the end* is not itself an answer: it opens the sale, and what settles the
     * session is the purchase that lands. Confirming waits for that, because a session
     * marked held with nothing behind it earns nothing for ever -- every money query
     * reaches the price through the purchase.
     *
     * This gates the confirm *inside* the picker, not the button that opens it: that press
     * asks the question rather than having to have answered it already. It waits only for
     * the options themselves to arrive, which is a different thing and lasts a moment.
     */
    const canHold: boolean = !needsAPurchase || choice.startsWith(PLAN);

    /**
     * The plan this session comes out of, when exactly one does.
     *
     * A question with one answer is not a question. The patient has paid for this very work
     * and holds one way to pay for it, so the press draws it down -- and the card says which
     * plan, above, so the therapist reads the decision rather than discovering it later on
     * the patient's file.
     */
    const onlyPlan: IPatientPurchase | null = matching.length === 1 ? matching[0] : null;

    /**
     * Headings appear only when there is more than one group to tell apart -- the rule the
     * booking dialog and the wizard's therapist step both follow.
     *
     * **The response's `otherPlans` is deliberately read nowhere.** It was, and had to be:
     * while a plan and the sessions it sells were two unrelated catalogue types, `matching`
     * could not hold a real package at all, so counting it alone would have ignored every
     * plan a patient owns. That is fixed underneath -- a plan now names the session it is a
     * block of -- so `matching` means what it says, and what is left in the other bucket is
     * work of a different discipline. A Kinetoterapie package is not an answer to "what pays
     * for this massage", it is a different sale.
     *
     * Nothing is unreachable by dropping it: the API still accepts any of the patient's
     * purchases, and `PUT /appointments/{id}/purchase` re-points a session already held. It
     * is merely off the path where it was doing harm.
     */
    const groupCount: number = (matching.length > 0 ? 1 : 0) + (payNow ? 1 : 0);

    const planGroup = (plans: IPatientPurchase[], heading: string): JSX.Element[] => {
        if (plans.length === 0) {
            return [];
        }

        return [
            ...(groupCount > 1
                ? [<ListSubheader key={`${heading}-header`}>{translate(heading)}</ListSubheader>]
                : []),
            ...plans.map(purchase => (
                <MenuItem key={purchase.id} value={`${PLAN}${purchase.id}`}>
                    {translate(ResourcesIds.APPOINTMENT_PURCHASE_OPTION, {
                        service: planLabel(purchase, translate, i18n.language),
                        remaining: bookableSessions(purchase),
                    })}
                </MenuItem>
            )),
        ];
    };

    const close = (): void => {
        setChoice('');
        setIsSelling(false);
        setIsChoosingPurchase(false);
        onClose();
    };

    /**
     * Picking *Pays at the end* opens the sale rather than answering the question.
     *
     * The clinic takes payment as the session ends, so this is a real sale on real terms --
     * a discount, an agreed price, a note -- and the dialog that already knows how to take
     * one is the patient's own. It opens on this appointment's service, therapist and day,
     * with the *record the session* tick withheld: the session is already in the diary, and
     * a second held row would double-count it.
     */
    const handleChoice = (value: string): void => {
        setChoice(value);

        if (value === PAY_AT_END) {
            setIsSelling(true);
        }
    };

    /**
     * @param purchaseId what pays for it, when the caller knows better than {@link choice}
     *                   does -- a sale that has just landed, whose id is not in state yet
     *                   and would not be by the time this ran.
     */
    const handleResolve = (status: AppointmentStatus, purchaseId?: number): void => {
        if (!appointment) {
            return;
        }

        const settlesWith: number | undefined = purchaseId
            ?? (choice.startsWith(PLAN) ? Number(choice.slice(PLAN.length)) : undefined);

        resolve.mutate(
            {
                appointmentId: appointment.id,
                status,
                // Named only when marking it held, and only when the row does not already
                // carry one: the clinic takes payment as the session ends, so this is the
                // moment the money is settled.
                ...(status === 'HELD' && settlesWith ? { purchaseId: settlesWith } : {}),
            },
            {
                onSuccess: () => {
                    showSnackbar(translate(ResourcesIds.APPOINTMENT_UPDATED), 'success');
                    close();
                    changed();
                },
                /*
                 * The API's own words, falling back to the generic line. Reopening is the
                 * case that made this worth doing: cancelling gives the hour back, so the
                 * API refuses to put a booking back into one that has been taken since --
                 * and "the appointment could not be updated" leaves a therapist staring at
                 * a slot that visibly has something else in it with no idea why.
                 */
                onError: (error: unknown) =>
                    showSnackbar(
                        splitApiError(error, [], translate,
                            ResourcesIds.APPOINTMENT_UPDATE_FAILED).formError
                            ?? translate(ResourcesIds.APPOINTMENT_UPDATE_FAILED),
                        'error'
                    ),
            }
        );
    };

    /**
     * The sale landed, so the session now has something to come out of -- and is held
     * against it in the same breath.
     *
     * The therapist pressed *Mark as held* to get here; the sale is how that press is
     * answered, not a detour from it. It used to only select the new plan and wait for a
     * second press, which was right while the picker opened with the card and the press
     * came last. Now that the press comes first, stopping short of the status change would
     * leave the verb they asked for undone.
     *
     * The id is passed rather than read back out of `choice`: the state set on the line
     * above is not visible until the next render, and this runs now.
     */
    const handleSold = (purchase: IPatientPurchase): void => {
        setIsSelling(false);
        setChoice(`${PLAN}${purchase.id}`);
        handleResolve('HELD', purchase.id);
    };

    /**
     * *Mark as held*, which is the moment what pays for the session becomes a real question.
     *
     * Three answers, in the order that the fewest presses:
     *
     * - **The row already names a purchase** -- a session booked against a package bought
     *   weeks ago. Nothing to ask; it holds.
     * - **One plan of this discipline** -- it draws that down. Re-selling somebody a session
     *   they have already paid for is the one mistake this flow can make and nothing
     *   afterwards shows it happened, so the plan wins; the card has already said which.
     * - **Several** -- the picker, which is the only case that genuinely has no single
     *   answer, and the only reason it still exists.
     * - **None** -- the sale itself, opened on this appointment's own service. A picker
     *   whose only option is *pays at the end* is a question with one answer, and the press
     *   that opens it has already been made.
     *
     * The same button is also the confirm once the picker is open, which is why the first
     * branch reads `isChoosingPurchase` too: pressing it there means a plan has been
     * chosen, since `canHold` is what keeps it disabled until one has.
     */
    const handleHoldPressed = (): void => {
        if (!needsAPurchase || isChoosingPurchase) {
            handleResolve('HELD');
            return;
        }

        if (onlyPlan) {
            handleResolve('HELD', onlyPlan.id);
            return;
        }

        if (matching.length > 1) {
            setIsChoosingPurchase(true);
            return;
        }

        setChoice(PAY_AT_END);
        setIsSelling(true);
    };

    /** Yes to a patient's request. It becomes an ordinary booking, and the patient is emailed. */
    const handleConfirm = (): void => {
        if (!appointment) {
            return;
        }

        confirm.mutate(appointment.id, {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.APPOINTMENT_REQUEST_CONFIRMED), 'success');
                close();
                changed();
            },
            // The API's own sentence: "not confirmed in time" above all, for a card left open
            // across the deadline.
            onError: (error: unknown) => showSnackbar(
                splitApiError(error, [], translate,
                    ResourcesIds.APPOINTMENT_REQUEST_ACTION_FAILED).formError
                    ?? translate(ResourcesIds.APPOINTMENT_REQUEST_ACTION_FAILED),
                'error'
            ),
        });
    };

    const handleRemoveBlock = (): void => {
        if (!appointment) {
            return;
        }

        removeBlock.mutate(appointment.id, {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.MY_CALENDAR_BLOCK_REMOVED), 'success');
                setIsRemovingBlock(false);
                close();
                changed();
            },
            onError: () => {
                showSnackbar(translate(ResourcesIds.MY_CALENDAR_BLOCK_REMOVE_FAILED), 'error');
                setIsRemovingBlock(false);
            },
        });
    };

    return (
        <>
            <Dialog
                open={!!appointment}
                onClose={close}
                fullWidth
                maxWidth="xs"
                fullScreen={isCompact}
            >
                <DialogTitle>
                    {appointment?.timeBlock
                        ? translate(ResourcesIds.APPOINTMENT_TIME_BLOCK)
                        : (
                            <PersonLabel
                                size="md"
                                person={{
                                    name: appointment?.patientName,
                                    pictureUrl: appointment?.patientPictureUrl,
                                }}
                            />
                        )}
                </DialogTitle>

                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        {/* Both dates when it spans days: a week of leave read as
                            "Fri · 14:00–10:00" would name neither its length nor its end. */}
                        {appointment && (appointment.startsAt.slice(0, 10) === appointment.endsAt.slice(0, 10)
                            ? `${formatDate(appointment.startsAt, i18n)} · `
                                + `${clockOf(appointment.startsAt)}–${clockOf(appointment.endsAt)}`
                            : `${formatDate(appointment.startsAt, i18n)} ${clockOf(appointment.startsAt)} – `
                                + `${formatDate(appointment.endsAt, i18n)} ${clockOf(appointment.endsAt)}`)}
                        {appointment?.serviceNameRo
                            ? ` · ${localised(appointment.serviceNameRo, appointment.serviceNameEn, i18n.language)}`
                            : ''}
                    </Typography>

                    {/* A block has no patient, no purchase and no status to answer for, so
                        none of what follows applies to it. The one thing it can be is
                        wrong, and this is the only place it is ever shown. */}
                    {appointment?.timeBlock && isStaff && (
                        <>
                            <Alert severity="info" sx={detailRowStyles}>
                                {translate(ResourcesIds.MY_CALENDAR_ADD_BLOCK_HINT)}
                            </Alert>

                            <Box sx={detailRowStyles}>
                                <Button
                                    size="small"
                                    color="error"
                                    variant="contained"
                                    disabled={removeBlock.isPending}
                                    onClick={() => setIsRemovingBlock(true)}
                                >
                                    {translate(ResourcesIds.MY_CALENDAR_BLOCK_REMOVE)}
                                </Button>
                            </Box>
                        </>
                    )}

                    {/* Marking a session held is also settling it: the clinic takes payment
                        as the session ends, or at the end of the first session of a package,
                        so the therapist names what it comes out of. Only once they have said
                        that is what they are doing, though -- opening a card is not
                        resolving one, and this used to be the first thing on it. */}
                    {isChoosingPurchase && (
                        <TextField
                            select
                            fullWidth
                            size="small"
                            sx={detailRowStyles}
                            label={translate(ResourcesIds.APPOINTMENT_WHICH_PURCHASE)}
                            value={choice}
                            onChange={event => handleChoice(event.target.value)}
                        >
                            {/* Grouped only once there is more than one group to tell
                                apart -- the rule the booking dialog's own select
                                follows. Only this discipline's plans are here; see
                                the note on `groupCount` for why the others are not. */}
                            {planGroup(matching, ResourcesIds.MY_CALENDAR_ADD_PLANS)}

                            {payNow ? [
                                ...(groupCount > 1 ? [
                                    <ListSubheader key="pay-at-end-header">
                                        {translate(ResourcesIds.MY_CALENDAR_ADD_NO_PURCHASE)}
                                    </ListSubheader>,
                                ] : []),
                                <MenuItem key={PAY_AT_END} value={PAY_AT_END}>
                                    {translate(ResourcesIds.APPOINTMENT_PAY_AT_END_OPTION, {
                                        service: localised(
                                            payNow.nameRo, payNow.nameEn, i18n.language),
                                        price: formatPrice(payNow.price),
                                    })}
                                </MenuItem>,
                            ] : []}
                        </TextField>
                    )}

                    {/* What the press is about to spend, when the press has already decided.
                        Said rather than asked: the patient has one way to pay for this very
                        work, so a dropdown offering it would be a question with one answer --
                        but drawing a plan down without a word is a money decision the
                        therapist would meet later on the patient's file rather than here. */}
                    {isStaff && !appointment?.timeBlock && appointment?.status === 'SCHEDULED'
                        && onlyPlan && !isChoosingPurchase && (
                        <Typography variant="body2" color="text.secondary" sx={detailRowStyles}>
                            {translate(ResourcesIds.APPOINTMENT_DRAWS_DOWN, {
                                service: planLabel(onlyPlan, translate, i18n.language),
                                remaining: bookableSessions(onlyPlan),
                            })}
                        </Typography>
                    )}

                    {/* A patient's request: answered by the therapist it names, and by nobody
                        else. The deadline is the API's, read off the row as `confirmBy`;
                        past it Confirm goes, since the API would refuse it, while Decline
                        stays -- saying no with a note beats the sweep saying it silently. */}
                    {isStaff && appointment && isPendingRequest(appointment) && (
                        canAnswer(appointment, user?.id ?? 0) ? (
                            <>
                                <Alert
                                    severity={lapsed ? 'warning' : 'info'}
                                    sx={detailRowStyles}
                                >
                                    {lapsed
                                        ? translate(ResourcesIds.APPOINTMENT_REQUEST_LAPSED)
                                        : translate(ResourcesIds.APPOINTMENT_REQUEST_ANSWER_HINT, {
                                            when: appointment.confirmBy
                                                ? formatRecordDateTime(appointment.confirmBy, i18n)
                                                : '',
                                        })}
                                </Alert>

                                <Box sx={detailRowStyles}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        disabled={confirm.isPending
                                            || lapsed}
                                        onClick={handleConfirm}
                                    >
                                        {translate(ResourcesIds.APPOINTMENT_REQUEST_CONFIRM)}
                                    </Button>
                                    <Button
                                        size="small"
                                        color="error"
                                        disabled={confirm.isPending}
                                        onClick={() => setIsDeclining(true)}
                                    >
                                        {translate(ResourcesIds.APPOINTMENT_REQUEST_DECLINE)}
                                    </Button>
                                </Box>
                            </>
                        ) : (
                            <>
                                <Alert severity="info" sx={detailRowStyles}>
                                    {translate(ResourcesIds.APPOINTMENT_REQUEST_OTHER_THERAPIST, {
                                        name: appointment.therapistName,
                                    })}
                                </Alert>

                                <Box sx={detailRowStyles}>
                                    <Button
                                        size="small"
                                        color="error"
                                        disabled={resolve.isPending}
                                        onClick={() => handleResolve('CANCELLED')}
                                    >
                                        {translate(ResourcesIds.APPOINTMENT_REQUEST_CANCEL)}
                                    </Button>
                                </Box>
                            </>
                        )
                    )}

                    {/* Final, and already told to the patient -- so it is explained rather than
                        offered for reopening. */}
                    {appointment?.status === 'DECLINED' && (
                        <Alert severity="info" sx={detailRowStyles}>
                            {appointment.decisionNote
                                ? translate(ResourcesIds.APPOINTMENT_DECLINED_NOTE, {
                                    note: appointment.decisionNote,
                                })
                                : translate(appointment.declinedAutomatically
                                    ? ResourcesIds.APPOINTMENT_DECLINED_AUTOMATICALLY
                                    : ResourcesIds.APPOINTMENT_DECLINED_PLAIN)}
                        </Alert>
                    )}

                    {/* Moving it is the one verb a patient may also have, so it sits in a
                        row of its own rather than among the resolving buttons below --
                        which are staff-only and would otherwise leave a patient looking at
                        a lone button in an empty group. Whether it is drawn at all is
                        canReschedule, and the API answers that question the same way. */}
                    {appointment && canReschedule(appointment, user?.id ?? 0, isStaff) && (
                        <Box sx={detailRowStyles}>
                            <Button
                                size="small"
                                variant={isStaff ? 'outlined' : 'contained'}
                                onClick={() => setIsMoving(true)}
                            >
                                {translate(ResourcesIds.APPOINTMENT_RESCHEDULE)}
                            </Button>
                        </Box>
                    )}

                    {/* Only a booking can be resolved -- a block of leave has nobody to
                        turn up. A row that already reached a terminal state is put back to
                        SCHEDULED first, which is how a mis-click is undone without deleting
                        a real appointment.

                        Staff only: every button in here is PUT /appointments/{id}/status,
                        which is THERAPIST/ADMIN, so a patient shown one is shown a button
                        that has never worked. */}
                    {isStaff && !appointment?.timeBlock && appointment && isResolvable(appointment) && (
                        <Box sx={detailRowStyles}>
                            {appointment.status === 'SCHEDULED' ? (
                                <>
                                    {/* This is the press that *asks* what pays for the
                                        session, so it no longer waits for an answer --
                                        only the confirm inside the picker does, on
                                        `canHold`.

                                        It does wait for the options themselves, which is
                                        a different thing and lasts a moment: what this
                                        press opens depends on whether the patient has a
                                        plan, and pressing before that is known would
                                        offer to sell a session somebody has already paid
                                        for. Not gated when the row already names a
                                        purchase -- that query never runs, so it would
                                        never settle. */}
                                    <Button
                                        variant="contained"
                                        size="small"
                                        disabled={
                                            resolve.isPending
                                            || (isChoosingPurchase && !canHold)
                                            || (needsAPurchase && !knowsWhatCouldPay)
                                        }
                                        onClick={handleHoldPressed}
                                    >
                                        {translate(ResourcesIds.APPOINTMENT_MARK_HELD)}
                                    </Button>
                                    <Button
                                        size="small"
                                        color="warning"
                                        disabled={resolve.isPending}
                                        onClick={() => handleResolve('NO_SHOW')}
                                    >
                                        {translate(ResourcesIds.APPOINTMENT_MARK_NO_SHOW)}
                                    </Button>
                                    <Button
                                        size="small"
                                        color="error"
                                        disabled={resolve.isPending}
                                        onClick={() => handleResolve('CANCELLED')}
                                    >
                                        {translate(ResourcesIds.APPOINTMENT_CANCEL)}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    size="small"
                                    disabled={resolve.isPending}
                                    onClick={() => handleResolve('SCHEDULED')}
                                >
                                    {translate(ResourcesIds.APPOINTMENT_REOPEN)}
                                </Button>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={close}>
                        {translate(ResourcesIds.CLOSE_DIALOG)}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* The sale a session paid for at the end of it needs. The patient's own sell
                dialog rather than a money field invented here: what the clinic actually
                agreed -- a discount, a price, a note -- belongs on a real sale, and there
                is one dialog that knows how to take one. */}
            {appointment?.patientId && (
                <AddPurchaseDialog
                    open={isSelling}
                    patientId={appointment.patientId}
                    /* Closing un-picks *pays at the end*, which is not an answer on its
                       own -- but only that. A sale that landed has already replaced it
                       with its own plan, and this runs immediately after: the dialog
                       announces the row and then closes itself. Read through the updater
                       rather than the closure, which still holds the old value. */
                    onClose={() => {
                        setIsSelling(false);
                        setChoice(current => (current === PAY_AT_END ? '' : current));
                    }}
                    onAdded={handleSold}
                    /* A prefill is passed even when the appointment names no service the
                       price list still holds, and the service is then simply absent from
                       it. That is not a formality: the prefill is also what unticks
                       *record the session*, and this session is already in the diary --
                       so opening the sale bare here would offer to write a second one. */
                    prefill={{
                        ...(payNow ? { serviceId: payNow.id } : {}),
                        therapistId: appointment.therapistId,
                        soldOn: appointment.startsAt.slice(0, 10),
                    }}
                />
            )}

            {/* Closes this one behind it: a move lands on a new hour, and the detail
                dialog it was opened from is showing the old one. */}
            <RescheduleDialog
                appointment={isMoving ? appointment : null}
                isStaff={isStaff}
                onClose={() => setIsMoving(false)}
                onMoved={() => {
                    setIsMoving(false);
                    close();
                    changed();
                }}
            />

            <DeclineRequestDialog
                appointment={isDeclining ? appointment : null}
                onClose={() => setIsDeclining(false)}
                onDeclined={() => {
                    showSnackbar(translate(ResourcesIds.APPOINTMENT_REQUEST_DECLINED), 'success');
                    setIsDeclining(false);
                    close();
                    changed();
                }}
            />

            {/* Asked before, not undone after: the row is gone from the database and there
                is nothing left to reopen, unlike a cancelled booking. */}
            <ConfirmationDialog
                isOpen={isRemovingBlock}
                onClose={() => setIsRemovingBlock(false)}
                onConfirm={handleRemoveBlock}
                title={translate(ResourcesIds.MY_CALENDAR_BLOCK_REMOVE_TITLE)}
                description={translate(ResourcesIds.MY_CALENDAR_BLOCK_REMOVE_DESCRIPTION)}
                confirmMessage={translate(ResourcesIds.MY_CALENDAR_BLOCK_REMOVE)}
                denyMessage={translate(ResourcesIds.CANCEL_TEXT)}
            />
        </>
    );
};
